import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronDown, 
  ChevronUp, 
  Check, 
  X, 
  Zap, 
  TrendingUp, 
  Crown,
  Shield,
  FileText,
  Lock,
  Server,
  Users,
  Database,
  Clock,
  Download,
  ExternalLink
} from 'lucide-react';

const SubscriptionSection = () => {
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [showTerms, setShowTerms] = useState(false);

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '$149.99',
      priceUnit: '/user/month',
      userRange: 'Up to 25 users',
      description: 'Perfect for small teams getting started with PLM',
      icon: Zap,
      color: 'blue',
      features: {
        users: 'Up to 25 Users',
        programs: '3 Programs',
        parts: '1,000 Parts',
        storage: '5 GB Storage',
        support: 'Email Support',
        uptime: '99.5% Uptime SLA'
      },
      included: [
        'BOM Management',
        'ECO Workflow',
        'Part Number Management',
        'Basic Reporting',
        'API Access (Limited)',
        'Standard Integrations'
      ],
      notIncluded: [
        'CDRL Management',
        'Requirements Traceability',
        'ERP Integration',
        'SSO/SAML',
        'Dedicated Support',
        'Custom Fields'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$249.99',
      priceUnit: '/user/month',
      userRange: '25+ users',
      description: 'For growing teams requiring advanced PLM capabilities',
      icon: TrendingUp,
      color: 'purple',
      popular: true,
      features: {
        users: '25+ Users',
        programs: 'Unlimited Programs',
        parts: '10,000 Parts',
        storage: '50 GB Storage',
        support: 'Phone & Email Support',
        uptime: '99.9% Uptime SLA'
      },
      included: [
        'Everything in Starter',
        'CDRL Management',
        'Requirements Traceability',
        'ERP Integration',
        'Advanced Reporting',
        'Full API Access',
        'Custom Fields',
        'AI Copilot'
      ],
      notIncluded: [
        'SSO/SAML',
        'Dedicated CSM',
        'On-Premise Option',
        'Custom Training'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      priceUnit: '',
      userRange: 'Unlimited users',
      description: 'For large organizations with complex PLM requirements',
      icon: Crown,
      color: 'cyan',
      features: {
        users: 'Unlimited Users',
        programs: 'Unlimited Programs',
        parts: 'Unlimited Parts',
        storage: 'Unlimited Storage',
        support: '24/7 Priority Support',
        uptime: '99.95% Uptime SLA'
      },
      included: [
        'Everything in Professional',
        'SSO/SAML Integration',
        'Dedicated Customer Success Manager',
        'On-Premise Deployment Option',
        'Custom Training Programs',
        'ITAR Compliance Controls',
        'DFARS 252.204-7012 Support',
        'Custom SLA Terms'
      ],
      notIncluded: []
    }
  ];

  const keyTerms = [
    {
      icon: Lock,
      title: 'Data Security',
      description: 'AES-256 encryption at rest, TLS 1.3 in transit, SOC 2 Type II certified'
    },
    {
      icon: Shield,
      title: 'Compliance',
      description: 'ISO 27001, NIST CSF, ITAR/EAR controls available for restricted data'
    },
    {
      icon: Database,
      title: 'Data Ownership',
      description: 'You retain full ownership of your data. Export anytime in standard formats'
    },
    {
      icon: Clock,
      title: 'Data Retention',
      description: '60-day post-termination export window, then secure deletion within 30 days'
    },
    {
      icon: Server,
      title: 'Infrastructure',
      description: 'US-based data centers with optional regional deployment'
    },
    {
      icon: Users,
      title: 'Support',
      description: 'Tiered support levels with up to 24/7 critical incident response'
    }
  ];

  const togglePlan = (planId) => {
    setExpandedPlan(expandedPlan === planId ? null : planId);
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-500/20',
        border: 'border-blue-500/30',
        text: 'text-blue-400',
        hover: 'hover:border-blue-400'
      },
      purple: {
        bg: 'bg-purple-500/20',
        border: 'border-purple-500/30',
        text: 'text-purple-400',
        hover: 'hover:border-purple-400'
      },
      cyan: {
        bg: 'bg-cyan-500/20',
        border: 'border-cyan-500/30',
        text: 'text-cyan-400',
        hover: 'hover:border-cyan-400'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <section className="py-12 md:py-20 bg-slate-900" id="subscription">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-heading font-bold text-white mb-4">
            Subscription Plans
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Per-seat pricing that scales with your team. All plans include core configuration management capabilities.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12 m-swipe">
          {plans.map((plan) => {
            const colors = getColorClasses(plan.color);
            const isExpanded = expandedPlan === plan.id;
            const Icon = plan.icon;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border ${colors.border} ${colors.hover} bg-slate-800/50 transition-all duration-300 ${
                  plan.popular ? 'ring-2 ring-purple-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="p-6">
                  {/* Plan Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      <p className="text-gray-500 text-sm">{plan.description}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-gray-400">{plan.priceUnit}</span>
                  </div>
                  
                  {/* User Range Badge */}
                  {plan.userRange && (
                    <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4 ${colors.bg} ${colors.text}`}>
                      {plan.userRange}
                    </div>
                  )}

                  {/* Key Features */}
                  <div className="space-y-2 mb-6">
                    {Object.values(plan.features).map((feature, idx) => (
                      <div key={feature?.id ?? feature?.partNumber ?? feature?.ecoNumber ?? feature?.uuid ?? feature?.key ?? feature?.name ?? feature?.title ?? feature?.label ?? `idx-${idx}`} className="flex items-center gap-2 text-gray-300 text-sm">
                        <Check className={`w-4 h-4 ${colors.text}`} />
                        {feature}
                      </div>
                    ))}
                  </div>

                  {/* Expand/Collapse Button */}
                  <button
                    onClick={() => togglePlan(plan.id)}
                    data-testid={`expand-plan-${plan.id}`}
                    className={`w-full py-2 rounded-lg border ${colors.border} ${colors.text} font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:bg-slate-700/50`}
                  >
                    {isExpanded ? 'Hide Details' : 'View All Features'}
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 border-t border-slate-700 space-y-4 animate-fadeIn">
                      <div>
                        <h4 className="text-white font-semibold mb-2 text-sm uppercase tracking-wider">Included</h4>
                        <div className="space-y-1">
                          {plan.included.map((item, idx) => (
                            <div key={item?.id ?? item?.partNumber ?? item?.ecoNumber ?? item?.uuid ?? item?.key ?? item?.name ?? item?.title ?? item?.label ?? `idx-${idx}`} className="flex items-center gap-2 text-gray-300 text-sm">
                              <Check className="w-4 h-4 text-green-400" />
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                      {plan.notIncluded.length > 0 && (
                        <div>
                          <h4 className="text-white font-semibold mb-2 text-sm uppercase tracking-wider">Not Included</h4>
                          <div className="space-y-1">
                            {plan.notIncluded.map((item, idx) => (
                              <div key={item?.id ?? item?.partNumber ?? item?.ecoNumber ?? item?.uuid ?? item?.key ?? item?.name ?? item?.title ?? item?.label ?? `idx-${idx}`} className="flex items-center gap-2 text-gray-500 text-sm">
                                <X className="w-4 h-4 text-gray-600" />
                                {item}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* CTA Button */}
                  <Link
                    to={plan.id === 'enterprise' ? '/contact' : `/signup?plan=${plan.id}`}
                    data-testid={`select-plan-${plan.id}`}
                    className={`mt-6 block w-full py-3 rounded-lg font-bold text-center transition-all ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-500/30'
                        : `${colors.bg} ${colors.text} border ${colors.border} hover:bg-opacity-30`
                    }`}
                  >
                    {plan.id === 'enterprise' ? 'Contact Sales' : 'Get Started'}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Consultation Fee Fine Print */}
        <p className="text-center text-gray-600 text-[10px] mt-4 mb-8 max-w-3xl mx-auto leading-relaxed">
          * All subscriptions require an initial implementation and consultation fee ranging from $10,000 to $15,000 depending on scope and complexity. 
          This one-time fee covers system configuration, data migration assistance, team onboarding, and initial training. 
          Contact sales for detailed pricing and custom implementation packages.
        </p>

        {/* Terms & Conditions Section */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
          <button
            onClick={() => setShowTerms(!showTerms)}
            data-testid="toggle-terms-section"
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-700/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-cyan-400" />
              <div>
                <h3 className="text-lg font-bold text-white">Terms, Conditions & Security</h3>
                <p className="text-gray-400 text-sm">Review our SaaS agreement, data handling, and security controls</p>
              </div>
            </div>
            {showTerms ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {showTerms && (
            <div className="px-6 pb-6 animate-fadeIn">
              {/* Key Terms Grid */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {keyTerms.map((term, idx) => {
                  const Icon = term.icon;
                  return (
                    <div key={term?.id ?? term?.partNumber ?? term?.ecoNumber ?? term?.uuid ?? term?.key ?? term?.name ?? term?.title ?? term?.label ?? `idx-${idx}`} className="bg-slate-900/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-5 h-5 text-cyan-400" />
                        <h4 className="text-white font-semibold">{term.title}</h4>
                      </div>
                      <p className="text-gray-400 text-sm">{term.description}</p>
                    </div>
                  );
                })}
              </div>

              {/* Contract Summary */}
              <div className="bg-slate-900/50 rounded-lg p-6 mb-6">
                <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-400" />
                  Baselinx PLM, LLC - Contract Highlights
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Subscription Tiers:</span> Professional, Enterprise, and Government options</p>
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Payment Terms:</span> Net 30 days, invoiced annually in advance (USD)</p>
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Price Adjustments:</span> Max 5% annual increase or CPI, 90-day advance notice</p>
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Auto-Renewal:</span> 60-day notice required for non-renewal</p>
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Data Classification:</span> Restricted, Confidential, Internal, Public tiers</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Data Portability:</span> STEP (AP214/AP242), IGES, PDF/A, XLSX, JSON, CSV</p>
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Security Incident:</span> 72-hour notification for confirmed breaches</p>
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Confidentiality:</span> 5-year obligation post-termination (trade secrets longer)</p>
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Liability Cap:</span> Limited to 12 months of subscription fees paid</p>
                    <p className="text-gray-300"><span className="text-cyan-400 font-semibold">ITAR/DFARS:</span> Compliance addendums available for defense contractors</p>
                  </div>
                </div>
              </div>

              {/* Download Contract Link */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg border border-cyan-500/20">
                <div className="flex items-center gap-3">
                  <Download className="w-6 h-6 text-cyan-400" />
                  <div>
                    <h4 className="text-white font-semibold">Baselinx PLM, LLC SaaS Agreement</h4>
                    <p className="text-gray-400 text-sm">Download our complete subscription agreement for review</p>
                  </div>
                </div>
                <a
                  href="https://customer-assets.emergentagent.com/job_397438fc-52a3-4e4c-8aa0-250fdeb6143e/artifacts/9acqrs7v_Baselinx%20PLM%20SaaS%20Agreement%20LLC.docx"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-contract-link"
                  className="flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold rounded-lg transition-colors whitespace-nowrap"
                >
                  <FileText className="w-4 h-4" />
                  Download Contract
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              {/* Additional Links */}
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                  Privacy Policy <ExternalLink className="w-3 h-3" />
                </Link>
                <Link to="/terms" className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                  Terms of Service <ExternalLink className="w-3 h-3" />
                </Link>
                <Link to="/data-handling" className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                  Data Handling Statement <ExternalLink className="w-3 h-3" />
                </Link>
                <Link to="/nist-compliance" className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                  NIST 800-171 Compliance <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SubscriptionSection;
